import {
  Country,
  Email,
  EmailSearchFilter,
  Language,
  Preferences,
  StartedTour,
  UsedFeature,
  UsedFeaturesSearchFilter,
  User,
  UserFields,
  UserSearchFilter,
  VisitSource,
} from "@imagine/shared-base";
import { BackendInterface, DataResponse } from "../..";
import { t } from "i18next";

const getLoggedInUser = async (backend: BackendInterface) => {
  // No reason to request user data if no token is stored
  const hasAccessToken = await backend.hasAccessToken();
  if (!hasAccessToken) {
    console.log("No tokens stored, user not logged in");
    return { success: false, errorCode: "no-token" };
  }
  const result = await backend.get("api/users/info");
  const response: DataResponse<{
    user: User;
    preferences: Preferences;
    tours: StartedTour[];
  }> = {
    success: result.success,
    apiVersion: result.version,
  };
  // Possible errors
  // 403: Access denied
  // 500: Server error
  if (result.success) {
    if (result.data.id === undefined) {
      console.error("Check database for users");
    }
    const user = {
      id: result.data.id,
      email: result.data.email,
      language: result.data.language,
      country: result.data.country,
      role: result.data.role,
      name: result.data.name,
      collaboratorId: result.data.collaboratorId,
      newsletterMarketing: result.data.newsletterMarketing,
    } as User;
    if (result.data.tokens !== undefined) {
      const tokens = result.data.tokens;
      await backend.saveTokens(tokens.accessToken, tokens.refreshToken);
    }
    try {
      let preferences: Preferences = {};
      if (result.data.preferences) {
        preferences = JSON.parse(result.data.preferences) as Preferences;
      }
      const tours: StartedTour[] = result.data.tours || [];
      response.data = { user, preferences, tours };
    } catch (e) {
      console.error(e);
      backend.captureException("Failed to parse user preferences", {
        "Server response": JSON.stringify(result),
      });
    }

    // return result.data as User;
  } else {
    if (result.status === 403) {
      // User is not logged in
    } else {
      console.error("Unexpected error when checking logged in user");
      console.error(result);
      backend.captureException("Unexpected error when checking logged in user");
    }
  }
  return response;
};

/**
 * Send log in request and saves returned JWT token if login succeeded. Returns user on success.
 */
const logIn = async (
  backend: BackendInterface,
  email: string,
  password: string,
  rememberMe: boolean,
) => {
  const data = {
    email: email,
    password: password,
    remember: rememberMe, // Not used for jwt, for kept for legacy reasons
  };

  const result = await backend.post("api/users/login", data);
  const response: DataResponse<{
    user: User;
    preferences: Preferences;
    tours: StartedTour[];
  }> = {
    success: result.success,
    apiVersion: result.version,
  };
  console.log(result);
  // Possible errors
  // 401: Login failed
  // 500: Server error
  // 400: Missing data (email or password)
  if (result.success && result.data) {
    // user.setUser(result.data);

    if (result.data.tokens !== undefined) {
      const tokens = result.data.tokens;
      await backend.saveTokens(tokens.accessToken, tokens.refreshToken);
    } else {
      console.error("No tokens received on login");
    }

    response.data = {
      user: {
        country: result.data.country,
        email: result.data.email,
        id: result.data.id,
        language: result.data.language,
        name: result.data.name,
        role: result.data.role,
        collaboratorId: result.data.collaboratorId,
        newsletterMarketing: result.data.newsletterMarketing,
      },
      preferences: result.data.preferences,
      tours: result.data.tours || [],
    };
  } else {
    if (result.status === 401) {
      response.errorMessage = t("users:loginFailed");
      response.errorCode = "failed";
    } else if (result.status === 409) {
      response.errorMessage = t("users:loginFailedMissingActivation");
      response.errorCode = "not-activated";
    } else {
      console.error(result);
      backend.captureException("Login error", {
        "Server response": JSON.stringify(result),
      });
      response.errorMessage = t("users:loginFailedServerError");
      response.errorCode = "failed";
    }
  }
  return response;
};

const resetPassword = async (backend: BackendInterface, email: string) => {
  const data = {
    email: email,
  };

  const result = await backend.post("api/users/request-reset-password", data);
  // Possible errors
  // 409: Reset failed, account not activated
  // 500: Server error
  // 400: Missing data (email or password)
  if (result.success) {
    return {
      success: true,
    };
  } else {
    if (result.status === 409) {
      return {
        errorMessage: t("users:resetFailedMissingActivation"),
        error: "not-activated",
      };
    } else {
      console.error(result);
      backend.captureException("Reset password error", {
        "Server response": JSON.stringify(result),
      });
      return {
        errorMessage: t("users:resetPasswordFailed"),
        error: "failed",
      };
    }
  }
};

const resendActivationEmail = async (
  backend: BackendInterface,
  email: string,
) => {
  const data = {
    email: email,
  };

  const result = await backend.post(
    "api/users/resend-activation-email/" + email,
    data,
  );

  const response: DataResponse<void> = {
    success: result.success,
  };
  // Possible errors
  // 404: Email does not exist
  // 409: Already activated
  // 500: Unable to send email
  // 400: Missing data (email)

  if (!result.success) {
    if (result.status === 409 || result.status === 404) {
      response.errorMessage = t("users:emailDoesNotExistOrAlreadyActivated");
      response.errorCode = "not-exist";
    } else {
      console.error(result);
      backend.captureException("Resend activation email error", {
        "Server response": JSON.stringify(result),
      });
      response.errorMessage = t("users:failedToResendActivationEmail");
      response.errorCode = "failed";
    }
  }
  return response;
};

const signUp = async (
  backend: BackendInterface,
  email: string,
  password: string,
  country?: Country,
  language?: Language,
  ipCountry?: string,
  collaboratorId?: number,
  eventId?: string,
) => {
  const data = {
    email: email,
    password: password,
    source: localStorage.getItem("source"),
    trackingId: localStorage.getItem("trackingId"),
    country: country,
    language: language,
    ipCountry: ipCountry,
    collaboratorId: collaboratorId,
    eventId: eventId,
  };
  const result = await backend.post("api/users/signup", data);
  const response: DataResponse<void> = {
    success: result.success,
  };
  // Possible errors
  // 403: User (email) already exists
  // 422: Invalid data (email format, password format)
  // 500: Server error
  // 400: Missing data (email or password)
  if (!result.success) {
    if (result.status === 409) {
      response.errorMessage = t("users:emailAlreadyExists");
    } else if (result.status === 422) {
      if (result.data.email === false) {
        response.errorMessage = t("users:invalidEmailFormat");
      }
      if (result.data.password === false) {
        response.errorMessage = t("users:passwordMin6Characters");
      }
      backend.captureException("Signup response error", {
        "Server response": JSON.stringify(result),
      });
      response.errorMessage = t("users:signUpServerError");
    } else {
      console.error(result);
      backend.captureException("Signup error", {
        "Server response": JSON.stringify(result),
      });
      response.errorMessage = t("users:signUpFailed");
    }
  }
  return response;
};

const updateUserProfile = async (
  backend: BackendInterface,
  email: string,
  name: string,
  language: string,
  country: string,
) => {
  const data = {
    email: email,
    name: name,
    language: language,
    country: country,
  };

  const result = await backend.post("api/users/update", data);
  const response: DataResponse<User> = {
    success: result.success,
  };

  // Possible errors
  // 500: Server error
  if (result.success) {
    if (result.data.tokens !== undefined) {
      const tokens = result.data.tokens;
      await backend.saveTokens(tokens.accessToken, tokens.refreshToken);
      response.data = result.data.user;
    }
  } else {
    if (result.status === 422) {
      response.errorMessage = t("users:invalidEmailFormat");
    } else if (result.status === 403) {
      response.errorMessage = t("users:emailAlreadyExists");
    } else {
      backend.captureException("Failed to save updated user data", {
        "Server response": JSON.stringify(result),
        "User data": data,
      });
      response.errorMessage = t("users:userUpdateFailed");
    }
  }
  return response;
};

const updatePassword = async (
  backend: BackendInterface,
  currentPassword: string,
  newPassword: string,
) => {
  const data = {
    currentPassword: currentPassword,
    newPassword: newPassword,
  };

  const result = await backend.post("api/users/update-password", data);
  const response: DataResponse<void> = {
    success: result.success,
  };
  console.warn(result);
  // Possible errors
  // 500: Server error
  if (!result.success) {
    backend.captureException("Failed to reset password", {
      "Server response": JSON.stringify(result),
    });
    response.errorMessage = t("users:failedToUpdatePassword");
  }
  return response;
};

/**
 * Update the user preferences
 * @param preferences The preferences object. No type is given, because this is project specific.
 * @returns True on success.
 */
const updatePreferences = async (
  backend: BackendInterface,
  preferences: Preferences,
) => {
  const result = await backend.post("api/users/preferences", preferences);
  const response: DataResponse<void> = {
    success: result.success,
  };
  if (!result.success) {
    backend.captureException("Failed to update user preferences", {
      result: JSON.stringify(result),
    });
    response.errorMessage = t("users:failedToUpdatePreferences");
  }
  return response;
};

/**
 * Get the user preferences.
 * @returns The preference object on success and undefined on failure.
 */
const getPreferences = async (backend: BackendInterface) => {
  const result = await backend.get("api/users/preferences");
  if (!result.success) {
    backend.captureException("Failed to get user preferences", {
      result: JSON.stringify(result),
    });
    return undefined;
  }
  return result.data;
};

const restorePassword = async (
  backend: BackendInterface,
  password: string,
  resetCode: string,
) => {
  const data = {
    password: password,
    resetCode: resetCode,
  };

  const result = await backend.post("api/users/reset-password", data);
  const response: DataResponse<void> = {
    success: result.success,
  };
  // Possible errors
  // 500: Server error
  if (!result.success) {
    if (result.status === 401) {
      // Expired or invalid code
      response.errorMessage = t("users:passwordNotRestoredTimeExpired");
    } else if (result.status === 422) {
      // Expired or invalid code
      response.errorMessage = t("users:passwordMin6Characters");
    } else {
      // Server error
      response.errorMessage = t("users:passwordNotRestoredServerError");
      backend.captureException(
        "Failed to restore password due to server error",
        {
          "Server response": JSON.stringify(result),
        },
      );
    }
  }
  return response;
};

const activateAccount = async (
  backend: BackendInterface,
  activationCode: string,
) => {
  const data = {
    activationCode: activationCode,
  };
  const result = await backend.post("api/users/activate", data);
  const response: DataResponse<void> = {
    success: result.success,
  };
  // Possible errors
  // 500: Server error
  if (!result.success) {
    if (result.status === 422) {
      // Expired or invalid code
      response.errorMessage = t("users:activationFailedInvalidActivationCode");
    } else {
      // Server error
      response.errorMessage = t("users:activationFailed");
      backend.captureException("Failed to activate account", {
        "Activation code": activationCode,
        "Server response": JSON.stringify(result),
      });
    }
  }
  return response;
};

const postUsedFeatures = async (
  backend: BackendInterface,
  userId: number,
  features: number[],
) => {
  const result = await backend.post(
    "api/users/log-features/" + userId,
    features,
  );
  const response: DataResponse<number[]> = {
    success: result.success,
    apiVersion: result.version,
  };
  if (!result.success) {
    backend.captureException("Failed to log features", {
      "Server response": JSON.stringify(result),
    });
    response.data = features;
  }
  return response;
};

const getUserOptions = async (backend: BackendInterface) => {
  const result = await backend.get("api/users/options");
  const response: DataResponse<User[]> = {
    success: result.success,
  };
  if (result.success && result.data) {
    response.data = result.data;
  } else {
    backend.captureException("Failed to get user options", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

const searchUsers = async (
  backend: BackendInterface,
  filter: UserSearchFilter,
) => {
  const cleanFilter: UserSearchFilter = {
    email: filter.email || undefined,
    country: filter.country || undefined,
    role:
      filter.role === undefined
        ? undefined
        : filter.role > 0
          ? filter.role
          : undefined,
    active: filter.active || undefined,
    sortBy: filter.sortBy || undefined,
    language: filter.language || undefined,
    startDate: filter.startDate || undefined,
    endDate: filter.endDate || undefined,
    source: filter.source || undefined,
    manualRole: filter.manualRole || undefined,
  };
  const result = await backend.post(
    "api/users/search",
    cleanFilter as Record<string, unknown>,
  );
  const response: DataResponse<User[]> = {
    success: result.success,
  };
  if (result.success && result.data) {
    response.data = result.data;
  } else {
    backend.captureException("Failed to search users", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

const updateUser = async (
  backend: BackendInterface,
  userId: number,
  user: UserFields,
) => {
  const result = await backend.post("api/users/update/admin/" + userId, user);
  const response: DataResponse<void> = {
    success: result.success,
  };
  if (!result.success) {
    backend.captureException("Failed to update user", {
      "Server response": JSON.stringify(result),
    });
    response.errorMessage = t("users:updateUserFailed");
  }
  return response;
};

const voteCountryExpansion = async (
  backend: BackendInterface,
  country: string,
  ip: string,
) => {
  if (country === "" || ip === "") return;
  const result = await backend.post("api/users/vote-country-expansion", {
    country: country,
    ip: ip,
  });
  const response: DataResponse<void> = {
    success: result.success,
  };
  if (!result.success) {
    backend.captureException("Failed to vote country expansion", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

const getEmails = async (
  backend: BackendInterface,
  filter: EmailSearchFilter,
) => {
  const result = await backend.post("api/users/emails", filter);
  const response: DataResponse<Email[]> = {
    success: result.success,
  };
  if (result.success && result.data) {
    response.data = result.data;
  } else {
    backend.captureException("Failed to get emails", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

const deleteUnactivatedUsers = async (backend: BackendInterface) => {
  const result = await backend.post("api/users/remove-unactivated", {});
  const response: DataResponse<void> = {
    success: result.success,
  };
  if (!result.success) {
    backend.captureException("Failed to delete unactivated users", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

/**
 * Deletes all plans, plant collections, private plants and private object for a user. The user itself is marked as deleted.
 * @param userId
 * @returns
 */
const deleteUser = async (backend: BackendInterface, userId: number) => {
  const result = await backend.post("api/users/delete/" + userId, {});
  const response: DataResponse<{ errors: number }> = {
    success: result.success,
  };
  console.log("Delete user", result);
  if (result.success && result.data) {
    response.data = result.data;
  }
  if (!result.success) {
    backend.captureException("Failed to delete user", {
      "Server response": JSON.stringify(result),
    });
  }

  return response;
};

const getUsedFeatures = async (
  backend: BackendInterface,
  filter: UsedFeaturesSearchFilter,
) => {
  const result = await backend.post("api/users/used-features", filter);
  const response: DataResponse<UsedFeature[]> = {
    success: result.success,
  };
  if (result.success && result.data) {
    response.data = result.data;
  } else {
    backend.captureException("Failed to get used features", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

/**
 * Update collaborator ID for currently logged in user.
 * @param collaboratorId
 * @returns
 */
const updateCollaboratorId = async (
  backend: BackendInterface,
  collaboratorId: number,
) => {
  const result = await backend.post("api/users/update", {
    collaboratorId: collaboratorId,
  });
  const response: DataResponse<void> = {
    success: result.success,
  };
  if (!result.success) {
    backend.captureException("Failed to update users collaborator ID", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

const getCollaboratorUsers = async (
  backend: BackendInterface,
  collaboratorId: number,
) => {
  const result = await backend.get("api/users/collaborator/" + collaboratorId);
  const response: DataResponse<User[]> = {
    success: result.success,
  };
  if (result.success && result.data) {
    response.data = result.data as User[];
  } else {
    backend.captureException("Failed to get collaborator users", {
      Result: JSON.stringify(result),
    });
  }

  return response;
};

const getAllSources = async (backend: BackendInterface) => {
  const result = await backend.get("api/users/sources");
  const response: DataResponse<VisitSource[]> = {
    success: result.success,
  };
  if (result.success && result.data) {
    response.data = result.data;
  } else {
    backend.captureException("Failed to get visit sources", {
      "Server response": JSON.stringify(result),
    });
  }
  return response;
};

const logOut = (backend: BackendInterface) => {
  backend.clearTokens();
};

export const UsersApi = {
  getLoggedInUser,
  updatePreferences,
  getPreferences,
  logIn,
  resetPassword,
  resendActivationEmail,
  signUp,
  updateUserProfile,
  updatePassword,
  restorePassword,
  activateAccount,
  postUsedFeatures,
  getUserOptions,
  searchUsers,
  updateUser,
  voteCountryExpansion,
  getEmails,
  deleteUnactivatedUsers,
  deleteUser,
  getUsedFeatures,
  updateCollaboratorId,
  getCollaboratorUsers,
  getAllSources,
  logOut,
};
